// Types shared by the server and the browser client. This package has no runtime dependencies in its
// main entry and must not import Node built-ins — it ships to the customer's browser (FR-5.15).

/** FR-5.6 */
export const FIELD_TYPES = [
  'text',
  'textarea',
  'email',
  'phone',
  'number',
  'select',
  'multiselect',
  'checkbox',
  'consent',
  'date',
  'url',
  'hidden',
] as const;
export type FieldType = (typeof FIELD_TYPES)[number];

/** FR-5.5 — canonical roles drive search, templating and Reply-To. */
export const CANONICAL_ROLES = ['name', 'email', 'phone', 'message', 'consent'] as const;
export type CanonicalRole = (typeof CANONICAL_ROLES)[number];

/** FR-5.7 — which rules apply to which type is defined in `RULES_BY_TYPE` (rules.ts). */
export interface FieldRules {
  minLength?: number;
  maxLength?: number;
  /** Inclusive lower bound for `number`. */
  min?: number;
  /** Inclusive upper bound for `number`. */
  max?: number;
  /** A regular expression source, matched against the whole value. */
  pattern?: string;
  /** Allowed values for `select` and `multiselect`. */
  options?: string[];
  /** Maximum number of selected values for `multiselect`. */
  maxItems?: number;
  /** Inclusive ISO date (YYYY-MM-DD) bounds for `date`. */
  minDate?: string;
  maxDate?: string;
  /**
   * Countries (ISO 3166-1 alpha-2, from `PHONE_REGIONS`) whose national format is tried, in this order,
   * for a phone number written without a country code; the first in which it is a valid number wins.
   * Empty or absent: only numbers written with `+` and a country code. Server-side only (spec §19 #72).
   */
  phoneRegions?: string[];
  /** The single-country form of `phoneRegions`, which schemas written before the list carry. A field has one or the other. */
  phoneRegion?: string;
}

/** FR-5.5 */
export interface FieldDefinition {
  key: string;
  label: string;
  type: FieldType;
  required?: boolean;
  pii?: boolean;
  role?: CanonicalRole;
  rules?: FieldRules;
}

/** FR-5.4 — the ordered field list of one form schema version. */
export interface FormSchema {
  fields: FieldDefinition[];
}

/** FR-5.8 */
export const UNKNOWN_FIELD_POLICIES = ['reject', 'store_as_extra', 'ignore'] as const;
export type UnknownFieldPolicy = (typeof UNKNOWN_FIELD_POLICIES)[number];

/** FR-5.9 — platform limits, not per-field rules. Exceeding one is `payload_too_large`. */
export interface ValidationLimits {
  maxFields: number;
  maxValueLength: number;
}

export const DEFAULT_LIMITS: ValidationLimits = { maxFields: 50, maxValueLength: 10_000 };

export type FieldValue = string | number | boolean | string[];

export const FIELD_ERROR_CODES = [
  'required',
  'invalid_type',
  'too_short',
  'too_long',
  'too_small',
  'too_large',
  'pattern',
  'not_an_option',
  'too_many_items',
  'invalid_email',
  'invalid_phone',
  'invalid_number',
  'invalid_date',
  'date_too_early',
  'date_too_late',
  'invalid_url',
  'must_accept',
  'unknown_field',
] as const;
export type FieldErrorCode = (typeof FIELD_ERROR_CODES)[number];

export interface FieldError {
  field: string;
  code: FieldErrorCode;
  /** Values interpolated into the localized message, e.g. `{ min: 3 }`. */
  params?: Record<string, string | number>;
}

export type CanonicalValues = Partial<Record<CanonicalRole, FieldValue>>;

export type ValidationResult =
  | {
      ok: true;
      /** Validated, coerced and normalized values of the schema's fields that were submitted. */
      values: Record<string, FieldValue>;
      /** Fields not in the schema, kept under the `store_as_extra` policy. */
      extra: Record<string, FieldValue>;
      canonical: CanonicalValues;
    }
  | { ok: false; reason: 'invalid'; errors: FieldError[] }
  | { ok: false; reason: 'too_large'; limit: keyof ValidationLimits };

/**
 * Injected, environment-specific behaviour. The browser passes none; the server passes a phone
 * normalizer backed by libphonenumber (too large for the browser budget — spec §17.2). A normalizer
 * returns the E.164 number, or `null` when the input is not a valid phone number.
 */
export interface Normalizers {
  /** `regions`: the countries to try, in order, for a national number (`phoneRegionsOf`); none means international only. */
  phone?: (value: string, regions: readonly string[]) => string | null;
}

export interface ValidateOptions {
  unknownFieldPolicy?: UnknownFieldPolicy;
  limits?: ValidationLimits;
  normalizers?: Normalizers;
}
